// Bilingual GDPR privacy policy + legal notice content for Magis Belgium
import { COMPANY } from "@/content";

export const LEGAL = {
  en: {
    privacy: {
      title: "Privacy Policy",
      updated: "Last updated: July 2026",
      intro:
        `${COMPANY.legal} ("Magis Belgium", "we", "us") is committed to protecting your personal data in accordance with Regulation (EU) 2016/679 (the "GDPR") and applicable Belgian data protection law. This Privacy Policy explains how we collect, use and safeguard your personal data when you use this website and our contact form.`,
      sections: [
        {
          h: "1. Data Controller",
          body: [
            `The controller responsible for your personal data is:`,
            `${COMPANY.legal}, ${COMPANY.address}. VAT ${COMPANY.vat}.`,
            `For any privacy-related request, contact us at ${COMPANY.email}.`,
          ],
        },
        {
          h: "2. What data we collect",
          body: [
            "When you submit our contact form, we collect the personal data you provide, namely: your full name, email address, organisation (optional) and the content of your message.",
            "We also process limited technical data (such as your IP address and browser information) that is automatically generated when you visit the website, for security and to ensure the website functions correctly.",
          ],
        },
        {
          h: "3. Purposes and legal bases",
          list: [
            "To respond to your enquiry and communicate with you about our services — legal basis: your consent and/or steps taken at your request prior to entering into a contract (Art. 6(1)(a) and 6(1)(b) GDPR).",
            "To manage and improve the website and ensure its security — legal basis: our legitimate interest (Art. 6(1)(f) GDPR).",
            "To comply with our legal obligations where applicable (Art. 6(1)(c) GDPR).",
          ],
        },
        {
          h: "4. Recipients and processors",
          body: [
            "Your data is accessed by authorised Magis Belgium personnel only. We use trusted service providers acting as data processors on our behalf, in particular an email delivery provider (Resend) used to transmit contact-form notifications, and our website hosting provider.",
            "We do not sell your personal data. Where a processor is located outside the European Economic Area, appropriate safeguards (such as Standard Contractual Clauses) are applied.",
          ],
        },
        {
          h: "5. Data retention",
          body: [
            "We retain contact-form data only for as long as necessary to handle your request and for the period required to comply with our legal obligations. Enquiry data is generally kept for a maximum of 24 months after our last contact, unless a longer period is required by law or to establish, exercise or defend legal claims.",
          ],
        },
        {
          h: "6. Your rights",
          body: ["Under the GDPR you have the right to:"],
          list: [
            "access your personal data;",
            "request rectification of inaccurate data;",
            "request erasure of your data ('right to be forgotten');",
            "request restriction of processing;",
            "object to processing based on legitimate interest;",
            "data portability;",
            "withdraw your consent at any time, without affecting the lawfulness of processing carried out before withdrawal.",
          ],
          after: `To exercise your rights, contact us at ${COMPANY.email}. You also have the right to lodge a complaint with the Belgian Data Protection Authority (Autorité de protection des données / Gegevensbeschermingsautoriteit), Rue de la Presse 35, 1000 Brussels — www.dataprotectionauthority.be.`,
        },
        {
          h: "7. Cookies",
          body: [
            "This website uses only essential/functional cookies required for its operation and, where applicable, anonymised analytics. No advertising or third-party tracking cookies are used without your consent. You can configure your browser to refuse cookies.",
          ],
        },
        {
          h: "8. Security",
          body: [
            "We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, alteration or disclosure.",
          ],
        },
        {
          h: "9. Changes to this policy",
          body: [
            "We may update this Privacy Policy from time to time. The applicable version is the one published on this page.",
          ],
        },
      ],
    },
    legal: {
      title: "Legal Notice",
      updated: "Last updated: July 2026",
      intro: "This legal notice sets out the identification details and terms of use of this website.",
      sections: [
        {
          h: "1. Website publisher",
          body: [
            `This website is published by ${COMPANY.legal}, a company incorporated under Belgian law.`,
            `Registered office: ${COMPANY.address}.`,
            `VAT / Company number: ${COMPANY.vat}.`,
            `Email: ${COMPANY.email}.`,
          ],
        },
        {
          h: "2. Publication director",
          body: [`The publication director is the legal representative of ${COMPANY.legal}.`],
        },
        {
          h: "3. Hosting",
          body: [
            "The website is hosted by a professional cloud hosting provider within the European Union. Hosting details are available on request via the email address above.",
          ],
        },
        {
          h: "4. Intellectual property",
          body: [
            "All content on this website (texts, graphics, logos, structure and design) is the property of Magis Belgium or its licensors and is protected by applicable intellectual property law. Any reproduction, representation, modification or exploitation, in whole or in part, without prior written authorisation is prohibited.",
          ],
        },
        {
          h: "5. Liability",
          body: [
            "Magis Belgium takes care to provide accurate and up-to-date information but cannot guarantee that the information is complete, exact or error-free. The information on this website is provided for general information purposes only and does not constitute legal, accounting or professional advice. Magis Belgium cannot be held liable for any direct or indirect damage arising from the use of this website.",
          ],
        },
        {
          h: "6. External links",
          body: [
            "This website may contain links to third-party websites. Magis Belgium exercises no control over those websites and accepts no responsibility for their content.",
          ],
        },
        {
          h: "7. Applicable law and jurisdiction",
          body: [
            "This website and this legal notice are governed by Belgian law. Any dispute relating to the use of this website falls under the exclusive jurisdiction of the courts of Brussels, Belgium.",
          ],
        },
      ],
    },
  },

  fr: {
    privacy: {
      title: "Politique de confidentialité",
      updated: "Dernière mise à jour : juillet 2026",
      intro:
        `${COMPANY.legal} (« Magis Belgium », « nous ») s'engage à protéger vos données à caractère personnel conformément au Règlement (UE) 2016/679 (le « RGPD ») et au droit belge applicable en matière de protection des données. La présente politique explique comment nous collectons, utilisons et protégeons vos données lorsque vous utilisez ce site et notre formulaire de contact.`,
      sections: [
        {
          h: "1. Responsable du traitement",
          body: [
            "Le responsable du traitement de vos données à caractère personnel est :",
            `${COMPANY.legal}, ${COMPANY.address}. TVA ${COMPANY.vat}.`,
            `Pour toute demande relative à la protection des données, contactez-nous à ${COMPANY.email}.`,
          ],
        },
        {
          h: "2. Données collectées",
          body: [
            "Lorsque vous soumettez notre formulaire de contact, nous collectons les données que vous fournissez : votre nom complet, votre adresse e-mail, votre organisation (facultatif) et le contenu de votre message.",
            "Nous traitons également des données techniques limitées (telles que votre adresse IP et les informations relatives à votre navigateur) générées automatiquement lors de votre visite, à des fins de sécurité et de bon fonctionnement du site.",
          ],
        },
        {
          h: "3. Finalités et bases légales",
          list: [
            "Répondre à votre demande et communiquer avec vous au sujet de nos services — base légale : votre consentement et/ou des mesures précontractuelles prises à votre demande (art. 6(1)(a) et 6(1)(b) RGPD).",
            "Gérer et améliorer le site et en assurer la sécurité — base légale : notre intérêt légitime (art. 6(1)(f) RGPD).",
            "Respecter nos obligations légales le cas échéant (art. 6(1)(c) RGPD).",
          ],
        },
        {
          h: "4. Destinataires et sous-traitants",
          body: [
            "Vos données sont accessibles uniquement au personnel autorisé de Magis Belgium. Nous faisons appel à des prestataires de confiance agissant en tant que sous-traitants, notamment un prestataire d'envoi d'e-mails (Resend) pour transmettre les notifications du formulaire de contact, ainsi que notre hébergeur.",
            "Nous ne vendons pas vos données. Lorsqu'un sous-traitant est situé hors de l'Espace économique européen, des garanties appropriées (telles que les clauses contractuelles types) sont appliquées.",
          ],
        },
        {
          h: "5. Conservation des données",
          body: [
            "Nous conservons les données du formulaire de contact uniquement le temps nécessaire au traitement de votre demande et pour la durée requise par nos obligations légales. Les données de demande sont généralement conservées 24 mois maximum après notre dernier contact, sauf durée plus longue requise par la loi ou pour la constatation, l'exercice ou la défense de droits en justice.",
          ],
        },
        {
          h: "6. Vos droits",
          body: ["Conformément au RGPD, vous disposez des droits suivants :"],
          list: [
            "accéder à vos données à caractère personnel ;",
            "demander la rectification de données inexactes ;",
            "demander l'effacement de vos données (« droit à l'oubli ») ;",
            "demander la limitation du traitement ;",
            "vous opposer au traitement fondé sur l'intérêt légitime ;",
            "la portabilité des données ;",
            "retirer votre consentement à tout moment, sans affecter la licéité du traitement effectué avant le retrait.",
          ],
          after: `Pour exercer vos droits, contactez-nous à ${COMPANY.email}. Vous avez également le droit d'introduire une réclamation auprès de l'Autorité de protection des données, Rue de la Presse 35, 1000 Bruxelles — www.autoriteprotectiondonnees.be.`,
        },
        {
          h: "7. Cookies",
          body: [
            "Ce site utilise uniquement des cookies essentiels/fonctionnels nécessaires à son fonctionnement et, le cas échéant, des statistiques anonymisées. Aucun cookie publicitaire ou de suivi tiers n'est utilisé sans votre consentement. Vous pouvez configurer votre navigateur pour refuser les cookies.",
          ],
        },
        {
          h: "8. Sécurité",
          body: [
            "Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, perte, altération ou divulgation.",
          ],
        },
        {
          h: "9. Modifications",
          body: [
            "Nous pouvons mettre à jour la présente politique. La version applicable est celle publiée sur cette page.",
          ],
        },
      ],
    },
    legal: {
      title: "Mentions légales",
      updated: "Dernière mise à jour : juillet 2026",
      intro: "Les présentes mentions légales précisent l'identité de l'éditeur et les conditions d'utilisation de ce site.",
      sections: [
        {
          h: "1. Éditeur du site",
          body: [
            `Ce site est édité par ${COMPANY.legal}, société de droit belge.`,
            `Siège social : ${COMPANY.address}.`,
            `Numéro de TVA / d'entreprise : ${COMPANY.vat}.`,
            `E-mail : ${COMPANY.email}.`,
          ],
        },
        {
          h: "2. Directeur de la publication",
          body: [`Le directeur de la publication est le représentant légal de ${COMPANY.legal}.`],
        },
        {
          h: "3. Hébergement",
          body: [
            "Le site est hébergé par un prestataire d'hébergement cloud professionnel au sein de l'Union européenne. Les détails de l'hébergement sont disponibles sur demande via l'adresse e-mail ci-dessus.",
          ],
        },
        {
          h: "4. Propriété intellectuelle",
          body: [
            "L'ensemble du contenu de ce site (textes, graphismes, logos, structure et design) est la propriété de Magis Belgium ou de ses concédants et est protégé par le droit de la propriété intellectuelle applicable. Toute reproduction, représentation, modification ou exploitation, totale ou partielle, sans autorisation écrite préalable est interdite.",
          ],
        },
        {
          h: "5. Responsabilité",
          body: [
            "Magis Belgium veille à fournir des informations exactes et à jour mais ne peut garantir leur exhaustivité ou leur absence d'erreur. Les informations de ce site sont fournies à titre d'information générale et ne constituent pas un conseil juridique, comptable ou professionnel. Magis Belgium ne saurait être tenue responsable de tout dommage direct ou indirect résultant de l'utilisation de ce site.",
          ],
        },
        {
          h: "6. Liens externes",
          body: [
            "Ce site peut contenir des liens vers des sites tiers. Magis Belgium n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu.",
          ],
        },
        {
          h: "7. Droit applicable et juridiction",
          body: [
            "Ce site et les présentes mentions légales sont régis par le droit belge. Tout litige relatif à l'utilisation de ce site relève de la compétence exclusive des tribunaux de Bruxelles, Belgique.",
          ],
        },
      ],
    },
  },
};
